import { useId } from "react";
import type { ReactNode } from "react";

import type { Field } from "./profile.model.ts";

/**
 * A titled box. The screen is made of these and nothing else.
 *
 * Not `lib/CollectionHead.tsx`, which is a heading with a switcher and a "view
 * all" link inside it — this head has an action on the right instead, and
 * bending that component to carry a button would give two screens a control
 * neither of them wanted.
 *
 * NAMED BY ITS OWN TITLE, which is what makes it a landmark rather than an
 * anonymous `<section>`. A bare section is not exposed as a region at all, so
 * "the Contact panel" was not a thing anyone — a screen reader user or a test —
 * could ask for, and this screen carries two `Edit` buttons the moment an
 * organisation is involved. Wiring `aria-labelledby` to the `<h2>` that is
 * already there costs one `useId` and gives every panel a name.
 */
export function Panel({
  title,
  note,
  action,
  children,
}: {
  title: string;
  note?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const titleId = useId();

  return (
    <section className="profile__panel" aria-labelledby={titleId}>
      <header className="profile__panel-head">
        <div>
          <h2 className="profile__panel-title" id={titleId}>
            {title}
          </h2>
          {note && <p className="profile__panel-note">{note}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

/**
 * A panel for something the registry does not hold.
 *
 * Reachable and specific, following `NotBuiltYet` in the collector shell: name
 * the missing piece rather than saying "coming soon", because a reader told
 * "there is no address on an ONDO account" understands the product and one told
 * "coming soon" suspects a bug.
 *
 * The dashed rule is `.tag--unverified`'s, and means what it means there: not a
 * failure state, an outline nobody has filled in yet.
 */
export function NotYet({ lead, detail }: { lead: string; detail: string }) {
  return (
    <div className="profile__notyet">
      <p className="profile__notyet-lead">{lead}</p>
      <p className="profile__notyet-detail">{detail}</p>
    </div>
  );
}

/** Label-and-value rows, with anything the registry does not hold set apart. */
export function FieldList({ fields }: { fields: Field[] }) {
  return (
    <dl className="profile__fields">
      {fields.map((field) => (
        <div className="profile__field" key={field.label}>
          <dt className="profile__field-label">{field.label}</dt>
          <dd
            className={`profile__field-value${
              field.absent ? " profile__field-value--absent" : ""
            }`}
          >
            {field.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
